import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { Plus, X, ChevronDown, ChevronRight, ChevronUp, GripVertical, Settings2 } from 'lucide-react';
import FilterBuilder from './FilterBuilder';

const SectionEditor = ({ section, parameters = [], onUpdate }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  
  const [entities, setEntities] = useState([]);
  const [entityFields, setEntityFields] = useState([]);
  const [expandedSections, setExpandedSections] = useState(['basic', 'data', 'fields']);

  useEffect(() => {
    loadEntities();
  }, []);

  useEffect(() => {
    if (section?.config?.entity) {
      loadEntityFields(section.config.entity);
    }
  }, [section?.config?.entity]);

  const loadEntities = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch('/api/reports/entities', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEntities(data.data || []);
      }
    } catch (error) {
      console.error('Error loading entities:', error);
    }
  };

  const loadEntityFields = async (entityName) => {
    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/reports/entities/${entityName}/fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEntityFields(data.data || []);
      }
    } catch (error) {
      console.error('Error loading entity fields:', error);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleConfigUpdate = (path, value) => {
    const newConfig = { ...section.config };
    const keys = path.split('.');
    let current = newConfig;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    onUpdate({ config: newConfig });
  };

  const handleFieldToggle = (fieldName, fieldInfo) => {
    const currentFields = section.config.fields || [];
    const fieldExists = currentFields.some(f => 
      typeof f === 'string' ? f === fieldName : f.name === fieldName
    );
    
    if (fieldExists) {
      // Remove field
      const newFields = currentFields.filter(f =>
        typeof f === 'string' ? f !== fieldName : f.name !== fieldName
      );
      handleConfigUpdate('fields', newFields);
    } else {
      // Add field with default formatting
      const newField = {
        name: fieldName,
        label: fieldInfo.label,
        type: fieldInfo.type,
        format: getDefaultFormat(fieldInfo.type)
      };
      handleConfigUpdate('fields', [...currentFields, newField]);
    }
  };

  const getDefaultFormat = (type) => {
    switch (type) {
      case 'number':
      case 'decimal':
        return { type: 'number', decimals: 2, thousandsSeparator: true, prefix: '', suffix: '' };
      case 'date':
        return { type: 'date', format: 'YYYY-MM-DD' };
      case 'datetime':
        return { type: 'datetime', format: 'YYYY-MM-DD HH:mm:ss' };
      case 'currency':
        return { type: 'currency', decimals: 2, currency: 'USD', symbol: '$' };
      default:
        return { type: 'text' };
    }
  };

  const handleMoveField = (index, direction) => {
    const currentFields = [...(section.config.fields || [])];
    if (direction === 'up' && index > 0) {
      [currentFields[index - 1], currentFields[index]] = [currentFields[index], currentFields[index - 1]];
    } else if (direction === 'down' && index < currentFields.length - 1) {
      [currentFields[index], currentFields[index + 1]] = [currentFields[index + 1], currentFields[index]];
    }
    handleConfigUpdate('fields', currentFields);
  };

  const handleRemoveField = (index) => {
    const currentFields = section.config.fields || [];
    handleConfigUpdate('fields', currentFields.filter((_, i) => i !== index));
  };

  const handleUpdateFieldFormat = (index, formatUpdate) => {
    const currentFields = [...(section.config.fields || [])];
    const field = currentFields[index];
    
    // Convert string to object if needed
    if (typeof field === 'string') {
      const fieldInfo = entityFields.find(f => f.name === field);
      currentFields[index] = {
        name: field,
        label: fieldInfo?.label || field,
        type: fieldInfo?.type || 'text',
        format: { ...getDefaultFormat(fieldInfo?.type), ...formatUpdate }
      };
    } else {
      currentFields[index] = {
        ...field,
        format: { ...field.format, ...formatUpdate }
      };
    }
    
    handleConfigUpdate('fields', currentFields);
  };

  const isFieldSelected = (fieldName) => {
    const currentFields = section.config.fields || [];
    return currentFields.some(f =>
      typeof f === 'string' ? f === fieldName : f.name === fieldName
    );
  };

  const getFieldObject = (field) => {
    if (typeof field === 'string') {
      const fieldInfo = entityFields.find(f => f.name === field);
      return {
        name: field,
        label: fieldInfo?.label || field,
        type: fieldInfo?.type || 'text',
        format: getDefaultFormat(fieldInfo?.type)
      };
    }
    return field;
  };

  const handleAddAggregation = () => {
    const currentAggregations = section.config.aggregations || [];
    const newAggregation = {
      field: entityFields[0]?.name || '',
      function: 'COUNT',
      alias: 'Count'
    };
    handleConfigUpdate('aggregations', [...currentAggregations, newAggregation]);
  };

  const handleRemoveAggregation = (index) => {
    const currentAggregations = section.config.aggregations || [];
    handleConfigUpdate('aggregations', currentAggregations.filter((_, i) => i !== index));
  };

  const handleUpdateAggregation = (index, field, value) => {
    const currentAggregations = [...(section.config.aggregations || [])];
    currentAggregations[index] = {
      ...currentAggregations[index],
      [field]: value
    };
    handleConfigUpdate('aggregations', currentAggregations);
  };

  const formatFieldPreview = (fieldObj) => {
    const format = fieldObj.format || {};
    
    switch (fieldObj.type) {
      case 'number':
      case 'decimal':
        let num = 1234.56;
        if (format.decimals !== undefined) {
          num = num.toFixed(format.decimals);
        }
        if (format.thousandsSeparator) {
          num = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return `${format.prefix || ''}${num}${format.suffix || ''}`;
      
      case 'currency':
        let curr = 1234.56;
        if (format.decimals !== undefined) {
          curr = curr.toFixed(format.decimals);
        }
        if (format.thousandsSeparator !== false) {
          curr = curr.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return `${format.symbol || format.prefix || '$'}${curr}`;
      
      case 'date':
        return format.format || 'YYYY-MM-DD';
      
      case 'datetime':
        return format.format || 'YYYY-MM-DD HH:mm:ss';
      
      default:
        return 'Sample Text';
    }
  };

  const CollapsibleSection = ({ id, title, children }) => {
    const isExpanded = expandedSections.includes(id);
    return (
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-3">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
        >
          <span className="font-medium text-gray-900 dark:text-gray-100">{title}</span>
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {isExpanded && (
          <div className="p-3 space-y-3 bg-white dark:bg-gray-900">
            {children}
          </div>
        )}
      </div>
    );
  };

  if (!section) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
        Select a section to edit its properties
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Basic Settings */}
      <CollapsibleSection id="basic" title="Basic Settings">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Title
          </label>
          <input
            type="text"
            value={section.config.title || ''}
            onChange={(e) => handleConfigUpdate('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
            placeholder="Section title"
          />
        </div>
      </CollapsibleSection>

      {/* Table-specific settings */}
      {section.type === 'table' && (
        <>
          <CollapsibleSection id="data" title="Data Source">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entity
              </label>
              <select
                value={section.config.entity || ''}
                onChange={(e) => handleConfigUpdate('entity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Select entity...</option>
                {entities.map(entity => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </select>
            </div>
          </CollapsibleSection>

          {section.config.entity && entityFields.length > 0 && (
            <CollapsibleSection id="fields" title="Fields & Formatting">
              <>
                {/* Selected Fields with Formatting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Selected Fields ({(section.config.fields || []).length})
                  </label>
                  <div className="space-y-2 mb-3">
                    {(section.config.fields || []).length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                        No fields selected. Select fields from the list below.
                      </div>
                    ) : (
                      (section.config.fields || []).map((field, index) => {
                        const fieldObj = getFieldObject(field);
                        return (
                          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800">
                            <div className="flex items-start gap-2">
                              {/* Drag Handle */}
                              <div className="flex flex-col gap-1 pt-1">
                                <button
                                  onClick={() => handleMoveField(index, 'up')}
                                  disabled={index === 0}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move up"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleMoveField(index, 'down')}
                                  disabled={index === (section.config.fields || []).length - 1}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Move down"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>

                              {/* Field Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                      {fieldObj.label}
                                    </span>
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                                      {fieldObj.type}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveField(index)}
                                    className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                    title="Remove field"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>

                                {/* Formatting Options */}
                                {(fieldObj.type === 'number' || fieldObj.type === 'decimal' || fieldObj.type === 'currency') && (
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <label className="block text-gray-600 dark:text-gray-400 mb-1">Decimals</label>
                                      <input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={fieldObj.format?.decimals || 2}
                                        onChange={(e) => handleUpdateFieldFormat(index, { decimals: parseInt(e.target.value) })}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-gray-600 dark:text-gray-400 mb-1">Prefix</label>
                                      <input
                                        type="text"
                                        value={fieldObj.format?.prefix || ''}
                                        onChange={(e) => handleUpdateFieldFormat(index, { prefix: e.target.value })}
                                        placeholder="$"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-gray-600 dark:text-gray-400 mb-1">Suffix</label>
                                      <input
                                        type="text"
                                        value={fieldObj.format?.suffix || ''}
                                        onChange={(e) => handleUpdateFieldFormat(index, { suffix: e.target.value })}
                                        placeholder="%"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                                      />
                                    </div>
                                    <div>
                                      <label className="flex items-center gap-2 text-gray-600 dark:text-gray-400 pt-5">
                                        <input
                                          type="checkbox"
                                          checked={fieldObj.format?.thousandsSeparator !== false}
                                          onChange={(e) => handleUpdateFieldFormat(index, { thousandsSeparator: e.target.checked })}
                                          className="rounded"
                                        />
                                        <span>1,000s</span>
                                      </label>
                                    </div>
                                  </div>
                                )}

                                {(fieldObj.type === 'date' || fieldObj.type === 'datetime') && (
                                  <div className="text-xs">
                                    <label className="block text-gray-600 dark:text-gray-400 mb-1">Date Format</label>
                                    <select
                                      value={fieldObj.format?.format || 'YYYY-MM-DD'}
                                      onChange={(e) => handleUpdateFieldFormat(index, { format: e.target.value })}
                                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100"
                                    >
                                      <option value="YYYY-MM-DD">2024-01-15</option>
                                      <option value="DD/MM/YYYY">15/01/2024</option>
                                      <option value="MM/DD/YYYY">01/15/2024</option>
                                      <option value="DD MMM YYYY">15 Jan 2024</option>
                                      <option value="MMMM DD, YYYY">January 15, 2024</option>
                                      {fieldObj.type === 'datetime' && (
                                        <>
                                          <option value="YYYY-MM-DD HH:mm:ss">2024-01-15 14:30:00</option>
                                          <option value="DD/MM/YYYY HH:mm">15/01/2024 14:30</option>
                                          <option value="MM/DD/YYYY hh:mm A">01/15/2024 02:30 PM</option>
                                        </>
                                      )}
                                    </select>
                                  </div>
                                )}

                                {/* Preview */}
                                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                  Preview: {formatFieldPreview(fieldObj)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Available Fields */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Available Fields
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                    {entityFields.map(field => (
                      <label key={field.name} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isFieldSelected(field.name)}
                          onChange={() => handleFieldToggle(field.name, field)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {field.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({field.type})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            </CollapsibleSection>
          )}

          <CollapsibleSection id="grouping" title="Grouping & Aggregation">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Group By
              </label>
              <select
                value={section.config.groupBy?.[0] || ''}
                onChange={(e) => handleConfigUpdate('groupBy', e.target.value ? [e.target.value] : [])}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">No grouping</option>
                {entityFields.map(field => (
                  <option key={field.name} value={field.name}>{field.label}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Aggregations
                </label>
                <button
                  onClick={handleAddAggregation}
                  className="text-xs btn btn-sm btn-secondary"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add
                </button>
              </div>
              
              {(section.config.aggregations || []).map((agg, index) => (
                <div key={index} className="flex gap-2 mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <select
                    value={agg.function}
                    onChange={(e) => handleUpdateAggregation(index, 'function', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                  >
                    <option value="COUNT">COUNT</option>
                    <option value="SUM">SUM</option>
                    <option value="AVG">AVG</option>
                    <option value="MIN">MIN</option>
                    <option value="MAX">MAX</option>
                  </select>
                  <select
                    value={agg.field}
                    onChange={(e) => handleUpdateAggregation(index, 'field', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                  >
                    {entityFields.map(field => (
                      <option key={field.name} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveAggregation(index)}
                    className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection id="filters" title="Filters">
            <FilterBuilder
              filters={section.config.filters || []}
              parameters={parameters}
              entityFields={entityFields}
              onChange={(filters) => handleConfigUpdate('filters', filters)}
            />
          </CollapsibleSection>
        </>
      )}

      {/* Chart-specific settings */}
      {section.type === 'chart' && (
        <>
          <CollapsibleSection id="chart-data" title="Chart Data">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Chart Type
              </label>
              <select
                value={section.config.chartType || 'bar'}
                onChange={(e) => handleConfigUpdate('chartType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="bar">Bar Chart</option>
                <option value="pie">Pie Chart</option>
                <option value="line">Line Chart</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entity
              </label>
              <select
                value={section.config.entity || ''}
                onChange={(e) => handleConfigUpdate('entity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Select entity...</option>
                {entities.map(entity => (
                  <option key={entity.id} value={entity.id}>{entity.name}</option>
                ))}
              </select>
            </div>

            {section.config.entity && entityFields.length > 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Group By
                  </label>
                  <select
                    value={section.config.groupBy || ''}
                    onChange={(e) => handleConfigUpdate('groupBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Select field...</option>
                    {entityFields.map(field => (
                      <option key={field.name} value={field.name}>{field.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Aggregation
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={section.config.aggregation?.function || 'COUNT'}
                      onChange={(e) => handleConfigUpdate('aggregation', {
                        ...section.config.aggregation,
                        function: e.target.value
                      })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="COUNT">COUNT</option>
                      <option value="SUM">SUM</option>
                      <option value="AVG">AVG</option>
                      <option value="MIN">MIN</option>
                      <option value="MAX">MAX</option>
                    </select>
                    <select
                      value={section.config.aggregation?.field || ''}
                      onChange={(e) => handleConfigUpdate('aggregation', {
                        ...section.config.aggregation,
                        field: e.target.value
                      })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">Select field...</option>
                      {entityFields.map(field => (
                        <option key={field.name} value={field.name}>{field.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Color
              </label>
              <input
                type="color"
                value={section.config.color || '#3b82f6'}
                onChange={(e) => handleConfigUpdate('color', e.target.value)}
                className="w-full h-10 rounded border border-gray-300 dark:border-gray-600"
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection id="chart-filters" title="Filters">
            <FilterBuilder
              filters={section.config.filters || []}
              parameters={parameters}
              entityFields={entityFields}
              onChange={(filters) => handleConfigUpdate('filters', filters)}
            />
          </CollapsibleSection>
        </>
      )}

      {/* Text-specific settings */}
      {section.type === 'text' && (
        <CollapsibleSection id="text-content" title="Content">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content
            </label>
            <textarea
              value={section.config.content || ''}
              onChange={(e) => handleConfigUpdate('content', e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter text content (HTML supported)..."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              You can use HTML tags and placeholders like {'{{fieldName}}'}
            </p>
          </div>
        </CollapsibleSection>
      )}

      {/* Styling */}
      <CollapsibleSection id="styling" title="Styling">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Custom CSS
          </label>
          <textarea
            value={section.styling?.css || ''}
            onChange={(e) => onUpdate({ styling: { ...section.styling, css: e.target.value } })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm dark:bg-gray-700 dark:text-gray-100"
            placeholder="border: 1px solid #ccc; padding: 16px;"
          />
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default SectionEditor;

